import { graphGetBlob, graphGetJson } from "../../email/services/graph";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import type { AttachmentInfo } from "../types";

interface AttachmentsResponse {
  readonly value?: readonly {
    /** "#microsoft.graph.fileAttachment", "…itemAttachment" or "…referenceAttachment". */
    readonly "@odata.type"?: string;
    readonly id?: string;
    readonly name?: string | null;
    readonly contentType?: string | null;
    readonly size?: number;
    readonly isInline?: boolean;
  }[];
}

interface RecurrenceResponse {
  readonly recurrence?: {
    readonly pattern?: { readonly type?: string; readonly interval?: number } | null;
  } | null;
}

interface BodyResponse {
  readonly body?: { readonly content?: string | null } | null;
}

/** The invite's full body, as HTML (calendarView gives only a short plain-text preview). */
export async function fetchEventBody(
  token: string,
  refresh: RefreshTokenFn | undefined,
  eventId: string,
): Promise<string> {
  const result = await graphGetJson<BodyResponse>(
    `/me/events/${encodeURIComponent(eventId)}?$select=body`,
    token,
    refresh,
    { Prefer: 'outlook.body-content-type="html"' },
  );
  return result.body?.content ?? "";
}

function attachmentKind(odataType: string | undefined): AttachmentInfo["kind"] {
  if (odataType?.endsWith("itemAttachment")) return "item";
  if (odataType?.endsWith("referenceAttachment")) return "reference";
  return "file";
}

/** Files attached to the invite. Inline images (signatures, logos) are left out. */
export async function fetchAttachments(
  token: string,
  refresh: RefreshTokenFn | undefined,
  eventId: string,
): Promise<AttachmentInfo[]> {
  const result = await graphGetJson<AttachmentsResponse>(
    `/me/events/${encodeURIComponent(eventId)}/attachments?$select=id,name,contentType,size,isInline`,
    token,
    refresh,
  );
  return (result.value ?? [])
    .filter((a) => !a.isInline)
    .map((a) => ({
      id: a.id ?? "",
      kind: attachmentKind(a["@odata.type"]),
      name: (a.name ?? "").trim() || "Attachment",
      contentType: a.contentType ?? "",
      size: a.size ?? 0,
    }));
}

/** An attached file's contents, for saving it. Only for `kind: "file"`. */
export async function fetchAttachmentFile(
  token: string,
  refresh: RefreshTokenFn | undefined,
  eventId: string,
  attachmentId: string,
): Promise<Blob> {
  return graphGetBlob(
    `/me/events/${encodeURIComponent(eventId)}/attachments/${encodeURIComponent(attachmentId)}/$value`,
    token,
    refresh,
  );
}

const PATTERN_WORD: Readonly<Record<string, [string, string]>> = {
  daily: ["daily", "days"],
  weekly: ["weekly", "weeks"],
  absoluteMonthly: ["monthly", "months"],
  relativeMonthly: ["monthly", "months"],
  absoluteYearly: ["yearly", "years"],
  relativeYearly: ["yearly", "years"],
};

/** "Repeats weekly" / "Repeats every 2 weeks", from the series' pattern; null when unknown. */
export function recurrenceLabel(pattern: { type?: string; interval?: number } | null | undefined): string | null {
  const words = pattern?.type ? PATTERN_WORD[pattern.type] : undefined;
  if (!words) return null;
  const interval = pattern?.interval ?? 1;
  return interval > 1 ? `Repeats every ${interval} ${words[1]}` : `Repeats ${words[0]}`;
}

/** The repeat pattern of the series an occurrence belongs to. */
export async function fetchRecurrenceLabel(
  token: string,
  refresh: RefreshTokenFn | undefined,
  seriesMasterId: string,
): Promise<string | null> {
  const result = await graphGetJson<RecurrenceResponse>(
    `/me/events/${encodeURIComponent(seriesMasterId)}?$select=recurrence`,
    token,
    refresh,
  );
  return recurrenceLabel(result.recurrence?.pattern);
}
